import { Action, RequestState } from 'modules/types/common';
import { switchAccountSuccess } from 'store/account/actions';
import { createReducer, Reducer } from '@reduxjs/toolkit';

type JSON = Record<string | number, any>;
export interface DefaultState<T = JSON> {
  isFetching: boolean;
  data?: T;
  error?: Error | string | null;
  lastUpdated?: string;
  requestState: RequestState;
}

export interface FetchingReducerState<T> extends DefaultState<T> {
  data?: T;
}

export const defaultInitialState = <T>(): FetchingReducerState<T> => ({
  isFetching: false,
  requestState: RequestState.INITIALIZED
});

export function setFetchingState<T>(
  state: FetchingReducerState<T>,
  action: Action,
  compare?: () => boolean
) {
  state.isFetching = true;
  state.error = null;
  state.requestState = RequestState.REQUESTING;
}

export function setFetchingFailState<T>(state: FetchingReducerState<T>, action: Action) {
  state.isFetching = false;
  state.error = action.error;
  state.requestState = RequestState.ERROR;
}

export function setFetchingSuccessState<T>(state: FetchingReducerState<T>, action: Action) {
  state.isFetching = false;
  state.error = null;
  state.data = action?.payload?.data || action.payload;
  state.requestState = RequestState.SUCCESS;
}

export function setFetchingCacheSuccessState<T>(state: FetchingReducerState<T>, action: Action) {
  state.data = action?.payload?.data || action.payload;
  state.lastUpdated = action?.payload?.cacheUpdatedAt;
}

export function createFetchReducer<T>(
  reducerType: string,
  resetOnSwitch?: boolean,
  extendReducers?: {
    [key: string]: (state: any, action: Action) => void;
  },
  extendedState?: any
): Reducer<FetchingReducerState<T>, Action> {
  return createReducer(
    {
      ...defaultInitialState<T>(),
      ...extendedState
    },
    {
      [reducerType]: setFetchingState,
      [`${reducerType}_FAIL`]: setFetchingFailState,
      [`${reducerType}_SUCCESS`]: setFetchingSuccessState,
      [`${reducerType}_CACHE_SUCCESS`]: setFetchingCacheSuccessState,
      [resetOnSwitch ? switchAccountSuccess.type : 'NO_TYPE']: () => defaultInitialState<T>(),
      ...extendReducers
    }
  );
}
